import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    useTheme,
    useMediaQuery,
    Box,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { AppList } from './AppList';
import { AppMenu } from './AppMenu';
import AppText from './AppText';
import AppLogo from './AppLogo';
import avis_logo_small from 'assets/images/avis_logo_small.png';

/**
 * AppNavigationBar - A responsive navigation bar with drawer support for mobile
 * @param {Object} props
 * @param {Array} props.menuItems - Navigation items configuration
 * @param {string} props.title - App title
 * @param {string} [props.position='fixed'] - AppBar position
 * @param {Object} [props.sx] - Additional styles
 */
const AppNavigationBar = forwardRef(({ menuItems, title, onItemClick, position = 'fixed', sx }, ref) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const renderDesktopMenu = (items) => (
        <AppMenu items={items} onItemClick={(item) => onItemClick?.(item)} />
    );

    const renderMobileMenu = (items, level = 0) => (
        <AppList items={items} variant="button" onItemClick={(item) => onItemClick?.(item)} />
    );

    return (
        <AppBar position={position} ref={ref} sx={sx}>
            <Toolbar>
                {isMobile && (
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}
                <Box sx={{ flexGrow: 1, flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
                    <AppLogo src={avis_logo_small} sx={{ width: 58, height: 58, mr: 1 }} />
                    <AppText text={title} variant="h6" />
                </Box>

                {!isMobile && renderDesktopMenu(menuItems)}

                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better mobile performance
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
                    }}
                >
                    <Toolbar /> {/* Spacer for fixed AppBar */}
                    {renderMobileMenu(menuItems)}
                </Drawer>
            </Toolbar>
        </AppBar>
    );
});

AppNavigationBar.propTypes = {
    menuItems: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        href: PropTypes.string,
        onClick: PropTypes.func,
        subItems: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string.isRequired,
            href: PropTypes.string,
            onClick: PropTypes.func,
        })),
    })).isRequired,
    title: PropTypes.string.isRequired,
    position: PropTypes.oneOf(['fixed', 'absolute', 'relative', 'static', 'sticky']),
    sx: PropTypes.object,
};

AppNavigationBar.displayName = 'AppNavigationBar';

export { AppNavigationBar };
